/**
 * WCAG contrast between two hex colours, for the chrome the renderer draws
 * itself (the well's frame and grid, the rail's labels): those are derived
 * from the board theme's own tokens, so which of a theme's colours reads
 * best against its background is computed, not assumed.
 */

function hexToRgb(hex: string): readonly [number, number, number] {
  const value = hex.replace('#', '');
  const full = value.length === 3 ? [...value].map((c) => c + c).join('') : value;
  return [
    Number.parseInt(full.slice(0, 2), 16),
    Number.parseInt(full.slice(2, 4), 16),
    Number.parseInt(full.slice(4, 6), 16),
  ];
}

/** WCAG relative luminance (0 black .. 1 white). */
function relativeLuminance(hex: string): number {
  const linear = (channel: number): number => {
    const c = channel / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const [r, g, b] = hexToRgb(hex);
  return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);
}

/** WCAG contrast ratio between two hex colours (1 .. 21). */
export function contrastRatio(hexA: string, hexB: string): number {
  const a = relativeLuminance(hexA);
  const b = relativeLuminance(hexB);
  const lighter = Math.max(a, b);
  const darker = Math.min(a, b);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * The body-text colour of a dark board theme. `tokens.foreground` is set only
 * by the light themes (blueprint, concrete — `packages/themes/src/types.ts`);
 * a dark theme leaves it out and the renderer reads this instead, for the
 * "NEXT" label and the well's frame.
 */
export const DEFAULT_DARK_FOREGROUND = '#E8ECF7';
